import './Layout.scss';

import { ButtonProps, Loader, MenuItemProps, TopNavigation, TopNavigationProps } from '@trackman/web-shared-components';
import { Can, Cannot } from 'components/Can';
import { DefaultRoutes, isCurrentRouteUnauth } from 'Routes';
import { ExpiredInvite, FailedInvite } from 'pages/Forbidden/Error';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { AppFooter } from '../AppFooter';
import { AppNotificationsToast } from 'components/ui';
import { AppPermissions } from 'utilities/Constants';
import AppSwitcher from '../AppSwitcher/AppSwitcher';
import { ApplicationUserRights } from 'stores/ApplicationUserRightsStore';
import LoggedOutInfoModal from '../LoggedOutInfoModal/LoggedOutInfoModal';
import { NoPermissions } from 'pages/NoPermissions';
import ProfileIconMenu from '../ProfileIconMenu/ProfileIconMenu';
import SharedSessionsPage from 'pages/SharedSessions';
import { observer } from 'mobx-react-lite';
import { t } from 'utilities/TranslationUtils';
import { useStores } from 'index';

type Props = {
  children: React.ReactNode;
};

type TopLevelMenuLabel = 'links.home' | 'links.management';

type Navigation = Array<
  {
    label: string;
  } & (
    | ({
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
        href: string;
        need: string[];
        submenu?: undefined;
      } & ButtonProps)
    | {
        onClick?: undefined;
        href?: undefined;
        need?: undefined;
        submenu: SubmenuItem[];
      }
  )
>;

type SubmenuItem = {
  label: string;
  onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  href: string;
  need: string[];
} & MenuItemProps;

type ValueOf<T> = T[keyof T];
type DefaultRoute = ValueOf<typeof DefaultRoutes>;

const routeToActiveLabel: Record<DefaultRoute, TopLevelMenuLabel> = {
  '/': 'links.home',
  '/users': 'links.management',
  '/dataIntegration': 'links.management',
  '/invite_expired': 'links.home',
  '/sharedsession': 'links.home',
};

const Layout = observer(({ children }: Props) => {
  const ss = useStores().startupStore;
  const history = useHistory();
  const location = useLocation();

  const [displayContent, setDisplayContent] = useState(false);

  useEffect(() => {
    if (!ss.isInitializing) setDisplayContent(true);
  }, [ss.isInitializing]);

  const menuSrtucture: Navigation = useMemo(() => {
    if (!ss.hasBearerToken && ss.isInitializing) return [];
    return [
      {
        label: t('links.home'),
        onClick: (e) => {
          e.preventDefault();
          history.push(DefaultRoutes.HomePage);
        },
        href: DefaultRoutes.HomePage,
        need: [],
      },
      {
        label: t('links.management'),
        hasAccess: AppPermissions.PortalAccess,
        submenu: [
          {
            label: t('links.users'),
            onClick: (e) => {
              e.preventDefault();
              history.push(DefaultRoutes.UsersPage);
            },
            href: DefaultRoutes.UsersPage,
            need: [AppPermissions.ManageUsers],
          },
          {
            label: t('links.dataIntegration'),
            onClick: (e) => {
              e.preventDefault();
              history.push(DefaultRoutes.DataIntegrationPage);
            },
            href: DefaultRoutes.DataIntegrationPage,
            need: [AppPermissions.ManageClients],
          },
        ]
      },
    ];
  }, [history, ss.isInitializing]);

  const activeNavigationLabel = useMemo(() => {
    const route = '/' + (location.pathname.split('/').filter((x) => x !== '')[0] ?? '');
    return routeToActiveLabel[route];
  }, [location.pathname]);

  const navigation: TopNavigationProps['navigation'] = useMemo(() => {
    const menuSrtuctureTemp: Navigation = [];

    menuSrtucture.forEach((item) => {
      if (item.need && ApplicationUserRights.IsAllowed(item.need)) menuSrtuctureTemp.push(item);
      if (item.submenu) {
        const submenuToMutate: SubmenuItem[] = [];
        menuSrtuctureTemp.push({ label: item.label, submenu: submenuToMutate });
        item.submenu.forEach((subItem) => {
          if (ApplicationUserRights.IsAllowed(subItem.need)) submenuToMutate.push(subItem);
        });
        if (submenuToMutate.length === 0) menuSrtuctureTemp.pop();
      }
    });
    return menuSrtuctureTemp;
  }, [menuSrtucture]);

  return (
    <div className='layout-content-container'>
      <div className='top-navigation'>
        <TopNavigation
          appName={'American Football Team Portal'}
          navigation={navigation}
          activeNavigationLabel={t(activeNavigationLabel)}
          utilities={
            ss.hasBearerToken && !ss.isInitializing
              ? [
                  { key: 'profile-icon-menu', component: <ProfileIconMenu setDisplayContent={setDisplayContent} /> },
                  {
                    key: 'app-switcher-icon-menu',
                    component: <AppSwitcher setDisplayContent={setDisplayContent} />,
                  },
                ]
              : []
          }
          animated
          displayContent={displayContent}
          onLogoClick={() => history.push(DefaultRoutes.HomePage)}
        />
      </div>
      {!displayContent && (
        <div className='spinner'>
          <Loader size='large' />
        </div>
      )}
      {!ss.isInitializing && (
        <div className={`content container-lg px-0 ${displayContent ? '' : 'content-hidden'}`}>
          {ss.hasBearerToken ? (
            <>
              <AppNotificationsToast />
              <Can need={AppPermissions.PortalAccess}>{children}</Can>
              <Cannot need={AppPermissions.PortalAccess}>
                <div className='forbidden-page'>
                  <Switch>
                    <Route path='/invite_expired' component={ExpiredInvite} />
                    <Route path='/invite_failed' component={FailedInvite} />
                    <Route component={NoPermissions} />
                  </Switch>
                </div>
              </Cannot>
              {ss.config && <LoggedOutInfoModal logoutUrl={ss.config.logoutUrl} />}
            </>
          ) : (
            <>
              <Switch>
                {/* Add Unauth Routes below */}
                <Route path={`${DefaultRoutes.SharedSessionsPage}/:shareCode`} component={SharedSessionsPage} />
              </Switch>
              {!isCurrentRouteUnauth() && ss.config && <LoggedOutInfoModal logoutUrl={ss.config.logoutUrl} />}
            </>
          )}
          <AppFooter />
        </div>
      )}
    </div>
  );
});

export default Layout;
